import {
  Button,
  FormField,
  Label,
  Modal,
  TextInput,
} from '@dev-spendesk/grapes';
import { type FormikProps } from 'formik';
import React, { useMemo } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { ColumnDisplayOptionsContainer } from './ColumnDisplayOptionsContainer';
import { CommonEditComponents } from './CommonEditComponents';
import type { JournalCodeFormValues } from './form';
import { type RowDisplayOption } from '../../../export';

type Props = {
  isOpen: boolean;
  title: string;
  description: string;
  isDeletable: boolean;
  onRemoveColumn: () => void;
  onClose: () => void;
  rowDisplayOptions?: RowDisplayOption[];
} & FormikProps<JournalCodeFormValues>;

export const JournalCodeColumnEditModal = ({
  isOpen,
  title,
  description,
  isDeletable,
  onRemoveColumn,
  onClose,
  // Formik
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  handleSubmit,
  rowDisplayOptions,
  ...rest
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: Props) => {
  const { t } = useTranslation('global');

  const showColumnDisplayOptions = useMemo(() => {
    return (
      rowDisplayOptions &&
      rowDisplayOptions.some((rowDisplayOption) => rowDisplayOption.active)
    );
  }, [rowDisplayOptions]);

  const noColumnDisplaySelected =
    rowDisplayOptions &&
    values.displayOptions &&
    !values.displayOptions.expense.active &&
    !values.displayOptions.analytical.active &&
    !values.displayOptions.vat.active &&
    !values.displayOptions.supplier.active;

  return (
    <Modal
      isOpen={isOpen}
      title={t('exports.columnEditModalTitle', { title })}
      subtitle={description}
      onClose={onClose}
      iconName="pen"
      actions={[
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={onClose}
        />,
        <Button
          key="saveChanges"
          text={t('misc.saveChanges')}
          isDisabled={noColumnDisplaySelected}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={(event: any) => {
            handleSubmit(event);
          }}
        />,
      ]}
    >
      <div className="text-left">
        <CommonEditComponents
          values={values}
          handleChange={handleChange}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
          handleSubmit={handleSubmit}
          {...rest}
        />

        <div className="mt-24 flex flex-col">
          <Label
            className="mb-8"
            label={t('bookkeep.settings.export.journalCodeOptions.header')}
          />
          {/* Card expense */}
          <FormField
            label={t('bookkeep.settings.export.journalCodeOptions.cardExpense')}
            className="mb-8"
            alertMessage={
              touched.cardExpense && Boolean(errors.cardExpense)
                ? errors.cardExpense
                : undefined
            }
          >
            <TextInput
              name="journalCodeCardExpense"
              id="journalCodeCardExpense"
              value={values.cardExpense || ''}
              onChange={(e) => {
                handleChange(e);
                setFieldValue('cardExpense', e.target.value);
              }}
              isInvalid={touched.cardExpense && Boolean(errors.cardExpense)}
            />
          </FormField>

          {/* Invoice */}
          <FormField
            label={t('bookkeep.settings.export.journalCodeOptions.invoice')}
            className="mb-8"
            alertMessage={
              touched.invoice && Boolean(errors.invoice)
                ? errors.invoice
                : undefined
            }
          >
            <TextInput
              name="journalCodeInvoice"
              id="journalCodeInvoice"
              value={values.invoice || ''}
              onChange={(e) => {
                handleChange(e);
                setFieldValue('invoice', e.target.value);
              }}
              isInvalid={touched.invoice && Boolean(errors.invoice)}
            />
          </FormField>

          {/* Expense Claim */}
          <FormField
            label={t(
              'bookkeep.settings.export.journalCodeOptions.expenseClaim',
            )}
            className="mb-8"
            alertMessage={
              touched.expenseClaim && Boolean(errors.expenseClaim)
                ? errors.expenseClaim
                : undefined
            }
          >
            <TextInput
              name="journalCodeExpenseClaim"
              id="journalCodeExpenseClaim"
              value={values.expenseClaim || ''}
              onChange={(e) => {
                handleChange(e);
                setFieldValue('expenseClaim', e.target.value);
              }}
              isInvalid={touched.expenseClaim && Boolean(errors.expenseClaim)}
            />
          </FormField>

          {/* Subscription */}
          <FormField
            label={t(
              'bookkeep.settings.export.journalCodeOptions.subscription',
            )}
            className="mb-8"
            alertMessage={
              touched.subscription && Boolean(errors.subscription)
                ? errors.subscription
                : undefined
            }
          >
            <TextInput
              name="journalCodeSubscription"
              id="journalCodeSubscription"
              value={values.subscription || ''}
              onChange={(e) => {
                handleChange(e);
                setFieldValue('subscription', e.target.value);
              }}
              isInvalid={touched.subscription && Boolean(errors.subscription)}
            />
          </FormField>
        </div>

        {showColumnDisplayOptions && (
          <FormField
            label={t(
              'bookkeep.settings.export.columnDisplayOptions.headerFormLabel',
            )}
            className="mt-24"
            alertMessage={
              noColumnDisplaySelected
                ? t(
                    `bookkeep.settings.export.columnDisplayOptions.selectColumnAlert`,
                  )
                : ''
            }
          >
            <ColumnDisplayOptionsContainer
              displayOptions={values.displayOptions}
              onDisplayOptionChange={(upd) =>
                setFieldValue('displayOptions', upd)
              }
              rowDisplayOptions={rowDisplayOptions ?? []}
              isAnalyticalColumn={false}
            />
          </FormField>
        )}

        {isDeletable && (
          <Button
            hasNegativeMargins
            className="mt-24"
            variant="tertiaryNeutral"
            iconName="circle-minus"
            iconPosition="left"
            text={t('exports.removeColumn')}
            onClick={onRemoveColumn}
          />
        )}
      </div>
    </Modal>
  );
};
